import React from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, NodeToolbar, Position, type NodeProps } from '@xyflow/react';

import { Icon, TextArea, type SelectOption } from '@/nova/components';

import { StudioSelect } from '../StudioSelect';

import { BEAT_ROLES } from '../../../storyboard';
import type { StoryboardBeatRole } from '@/store/slices/creativeJobs';
import { useCanvasActions } from '../context';
import type { BeatFlowNode } from '../graph';
import {
  Card,
  ErrorNote,
  FieldSettle,
  FocusScope,
  HeaderActions,
  HeaderRow,
  NoDrag,
  PlaceholderText,
  PlayBadge,
  Pill,
  PromoChip,
  StaleNote,
  Thumb,
  ThumbEmpty,
  ThumbImg,
  ToolButton,
  ToolbarRow,
} from './nodes.styles';

/**
 * A beat in the storyboard: its dramatic role, the motion that carries F_i to F_{i+1}, and its
 * duration. A NodeToolbar above the card moves or removes the beat (bounds enforced by the data
 * flags). All inputs sit in a NoDrag wrapper so editing never pans the canvas.
 *
 * The card is ONE element across every phase, so composing -> editable never remounts or reflows:
 *  - `data.composing === 'skeleton'` (the model is still writing, or this beat has not been reached yet):
 *    the REAL fields render empty + disabled, and a soft glow breathes on the card (Card `$seedIndex`).
 *  - `data.composing === 'typing'`: the role/duration values settle in (FieldSettle highlight), and the
 *    motion prose types into its real, disabled textarea (`data.typedMotion`).
 *  - `data.composing` undefined: the normal editable card (move/remove toolbar + regenerate/view controls).
 */
export const BeatNode = ({ data }: NodeProps<BeatFlowNode>): React.ReactElement => {
  const { t } = useTranslation();
  const actions = useCanvasActions();
  const { beat, index } = data;
  const beatNumber = index + 1; // 1-based beat, matching regenerateFrom/viewBeat + the stage ids

  const composing = data.composing; // 'skeleton' | 'typing' | undefined
  const isComposing = Boolean(composing);
  // A field's value "lands" (one-shot highlight) the moment the beat is being typed.
  const landed = composing === 'typing';

  // The Motion prompt is edited inline. React Flow delivers node data to a custom node a render LATE (it
  // syncs the `nodes` prop into its internal store via its own effect), so a controlled textarea bound
  // straight to `beat.motion_intent` reads a STALE value on the first commit after a keystroke, then React
  // rewrites the DOM and slams the caret to the end on every keypress. Fix: render the field from LOCAL
  // state (updated synchronously in onChange), and adopt external changes to the beat (an LLM refine,
  // reset-from-template, cached-draft load) only while the field is NOT focused. During compose the field is
  // disabled, so its rendered value comes from `motionValue` below (typed prose), NOT this local state; but
  // the sync still runs, pre-loading `motion` with the final refined text so the hand-off to editable is seamless.
  const [motion, setMotion] = React.useState(beat.motion_intent);
  const editingMotion = React.useRef(false);
  if (!editingMotion.current && beat.motion_intent !== motion) {
    setMotion(beat.motion_intent);
  }

  // Self-gate the Regenerate control: a click can never slip through while a run is in flight, or while a
  // mature primary art style blocks video renders (the disabled state only styles the button).
  const regenerateDisabled = data.running || actions.renderBlocked;
  const onRegenerate = (): void => {
    if (regenerateDisabled) return;
    actions.regenerateFrom(beatNumber);
  };

  const roleOptions: SelectOption[] = BEAT_ROLES.map((role) => ({
    value: role,
    label:
      role === 'hook'
        ? t('Hook')
        : role === 'offer'
          ? t('Offer')
          : role === 'proof'
            ? t('Proof')
            : t('Call to action'),
  }));

  const durationOptions: SelectOption[] = actions.videoDurations.map((d) => ({
    value: String(d),
    label: t('{{n}} seconds', { n: d }),
  }));

  const onRoleChange = (value: string): void => {
    actions.updateBeat(index, { role: value as StoryboardBeatRole });
  };

  // Field values per phase. Seeding shows empty placeholders; typing fills (the motion types in char by
  // char); editable reads the beat / local motion state. A native <select> can't animate its options, so a
  // dropdown "lands" its value with the FieldSettle highlight ring instead.
  const roleValue = composing === 'skeleton' ? '' : beat.role;
  const durationValue = composing === 'skeleton' ? '' : String(beat.duration_seconds);
  const motionValue = composing === 'typing' ? (data.typedMotion ?? '') : composing === 'skeleton' ? '' : motion;
  const fieldPlaceholder = composing === 'skeleton' ? '…' : undefined;

  return (
    <>
      {isComposing ? null : (
        <NodeToolbar position={Position.Top}>
          <ToolbarRow>
            <ToolButton
              type="button"
              aria-label={t('Move beat left')}
              disabled={!data.canMoveLeft}
              onClick={() => actions.moveBeat(index, -1)}
            >
              <Icon name="arrow-left" size={16} />
            </ToolButton>
            <ToolButton
              type="button"
              $flip
              aria-label={t('Move beat right')}
              disabled={!data.canMoveRight}
              onClick={() => actions.moveBeat(index, 1)}
            >
              <Icon name="arrow-left" size={16} />
            </ToolButton>
            <ToolButton
              type="button"
              $danger
              aria-label={t('Remove beat')}
              disabled={!data.canRemove}
              onClick={() => actions.removeBeat(index)}
            >
              <Icon name="trash" size={16} />
            </ToolButton>
          </ToolbarRow>
        </NodeToolbar>
      )}

      <Card
        $width={260}
        $status={data.status}
        $enter
        $seedIndex={composing === 'skeleton' ? index : undefined}
      >
        {/* Left: inbound from the previous beat / brand. Top: inbound from this beat's key visual above. */}
        <Handle type="target" position={Position.Left} id="in" />
        <Handle type="target" position={Position.Top} id="kv" />
        <HeaderRow>
          <Pill text="Beat {{n}}" placeholders={{ n: beatNumber }} />
          {isComposing ? null : (
            <HeaderActions>
              <ToolButton
                type="button"
                aria-label={t('Regenerate')}
                title={actions.renderBlocked ? t("Mature content isn't compatible with video generation.") : undefined}
                disabled={regenerateDisabled}
                onClick={onRegenerate}
              >
                <Icon name="refresh" size={16} />
              </ToolButton>
              <ToolButton type="button" aria-label={t('View beat')} onClick={() => actions.viewBeat(beatNumber)}>
                <Icon name="eye" size={16} />
              </ToolButton>
            </HeaderActions>
          )}
        </HeaderRow>
        {data.promotionIndex != null ? (
          data.promotionName ? (
            <PromoChip raw text={data.promotionName} />
          ) : (
            <PromoChip text="Promo {{n}}" placeholders={{ n: data.promotionIndex + 1 }} />
          )
        ) : null}
        <Thumb
          $ratio={data.thumbRatio}
          $dim={data.status === 'stale'}
          disabled={isComposing}
          aria-label={t('Play beat {{n}}', { n: beatNumber })}
          onClick={() => {
            if (isComposing) return;
            actions.viewBeat(beatNumber);
          }}
        >
          {data.poster ? (
            <>
              <ThumbImg src={data.poster} alt="" />
              {data.clipUrl ? <PlayBadge aria-hidden="true" /> : null}
            </>
          ) : (
            <ThumbEmpty>
              <Icon name="video" size={20} />
              <PlaceholderText text="Preview appears after you generate" />
            </ThumbEmpty>
          )}
        </Thumb>
        {data.status === 'stale' && !isComposing ? <StaleNote text="Needs regenerating" /> : null}
        <NoDrag>
          <FieldSettle $order={0} data-landed={landed}>
            <StudioSelect
              label={t('Role')}
              value={roleValue}
              placeholder={fieldPlaceholder}
              disabled={isComposing}
              onChange={onRoleChange}
              options={roleOptions}
            />
          </FieldSettle>
          <FieldSettle $order={1} data-landed={landed}>
            <FocusScope
              onFocus={() => {
                editingMotion.current = true;
              }}
              onBlur={() => {
                editingMotion.current = false;
                if (beat.motion_intent !== motion) setMotion(beat.motion_intent);
              }}
            >
              <TextArea
                label={t('Motion')}
                value={motionValue}
                disabled={isComposing}
                onChange={(value) => {
                  if (isComposing) return;
                  setMotion(value);
                  actions.updateBeat(index, { motion_intent: value });
                }}
                rows={3}
                autoGrow
              />
            </FocusScope>
          </FieldSettle>
          <FieldSettle $order={2} data-landed={landed}>
            <StudioSelect
              label={t('Duration')}
              value={durationValue}
              placeholder={fieldPlaceholder}
              disabled={isComposing}
              onChange={(value) => actions.updateBeat(index, { duration_seconds: Number(value) })}
              options={durationOptions}
            />
          </FieldSettle>
        </NoDrag>
        {data.error && !isComposing ? <ErrorNote raw text={data.error} /> : null}
        <Handle type="source" position={Position.Right} id="out" />
      </Card>
    </>
  );
};

export default BeatNode;
